import type { QueryKey, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../context/context';
import type { Consent, ConsentPostRequest } from '../data-contract/terms';
import { consentSchema } from '../data-contract/terms';

export const consentQueryKey: QueryKey = [`/consent`, 'GET'];

export const useConsentQuery = (): UseQueryResult<Consent> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        queryFn: async (): Promise<Consent> => {
            const result = await validatingCobrainerApiFetcher.get(consentSchema, {
                pathname: `/consent`,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: consentQueryKey,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const useConsentMutation = (): UseMutationResult<void, unknown, ConsentPostRequest> => {
    const queryClient = useQueryClient();

    const { cobrainerApiFetcher } = useContext(ApplicationContext);

    // Invalidate all queries after consent because they probably have failed with 403 before
    return useMutation({
        mutationFn: async (payload: ConsentPostRequest): Promise<void> => {
            const result = await cobrainerApiFetcher.postJson({
                pathname: `/consent`,
                payload,
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] !== 'feature-flags',
            }),
    });
};
